import React from 'react';
import { pricingTypes } from 'utils/constant';
import './style.css';
import { formatAmount } from 'utils/services';

const PricingModelBreakdown = ({ pricingModel, index, currency }) => {
  const modelNumber = index + 1;
  const isComposite = pricingModel?.charge_type === 'composite';

  const formate = (amount) => {
    if (amount === null || amount === undefined) return '0';
    return amount?.toLocaleString?.('en-US', { maximumSignificantDigits: 21 });
  };

  const getComponentTaxes = (component) => {
    const taxes = component?.taxes || [];
    return taxes.filter((tax) => tax?.inclusive !== true);
  };

  const formatTaxValue = (tax) => {
    if (tax?.type === 'percentage') return `${tax?.value}%`;
    return `${currency} ${formatAmount(tax?.value, currency)}`;
  };

  const calculateTaxAmount = (tax, component) => {
    if (tax?.amount !== undefined && tax?.amount !== null) return Number(tax?.amount);

    const base = component?.amount_without_taxes ?? 0;
    const value = Number(tax?.value) || 0;

    if (tax?.type === 'fixed') {
      return tax?.inclusive ? 0 : value;
    }

    if (tax?.type === 'percentage') {
      if (tax?.inclusive) {
        return (base * value) / (100 + value);
      }
      return (base * value) / 100;
    }

    return 0;
  };

  const getOperatorBadgeLabel = () => {
    const operator = pricingModel?.combination_operator;
    if (operator === 'OR' && pricingModel?.or_strategy) {
      return `OR (${pricingModel?.or_strategy?.toUpperCase()})`;
    }
    return operator || '';
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return 'N/A';
    return string?.charAt(0)?.toUpperCase() + string?.slice(1);
  };

  const renderSummaryTable = () => {
    return (
      <div className='invoice-summary-table'>
        <div className='invoice-table-header'>
          <div className='invoice-table-cell header-cell'>Summary</div>
          <div className='invoice-table-cell header-cell'>Charged Amount</div>
        </div>

        <div className='invoice-table-row'>
          <div className='invoice-table-cell'>Base Rate</div>
          <div className='invoice-table-cell'>{`${currency} ${formatAmount(pricingModel?.base_amount, currency)}`}</div>
        </div>

        {pricingModel?.components?.map((component, idx) => {
          const hasOperatorAfter = isComposite && idx < pricingModel?.components?.length - 1 && pricingModel?.combination_operator;
          const hasOperatorBefore = isComposite && idx > 0 && pricingModel?.combination_operator;

          return (
            <React.Fragment key={idx}>
              <div className={`invoice-table-row bold-row${hasOperatorAfter ? ' summary-row-before-operator' : ''}${hasOperatorBefore ? ' summary-row-after-operator' : ''}`}>
                <div className='invoice-table-cell ml-4'>Total for {capitalizeFirstLetter(component?.meter_name)}</div>
                <div className='invoice-table-cell'>{`${currency} ${formatAmount(component?.amount, currency)}`}</div>
              </div>
              {hasOperatorAfter && (
                <div className='invoice-summary-operator-divider'>
                  <span className='component-operator-badge'>{getOperatorBadgeLabel()}</span>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <div className='invoice-table-row total-row'>
          <div className='invoice-table-cell'>Payable Total</div>
          <div className='invoice-table-cell'>{`${currency} ${formatAmount(pricingModel?.total_amount, currency)}`}</div>
        </div>
      </div>
    );
  };

  const renderTaxesTable = (component) => {
    const taxes = getComponentTaxes(component);
    if (!taxes?.length) return null;

    return (
      <div className='taxes-section'>
        <div className='taxes-heading'>Additional Taxes</div>
        <div className='tier-table-wrapper'>
          <table className='invoice-tier-table taxes-table'>
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {taxes.map((tax, taxIdx) => (
                <tr key={tax?.key || taxIdx}>
                  <td>{capitalizeFirstLetter(tax?.key)}</td>
                  <td>{formatTaxValue(tax)}</td>
                  <td>{`${currency} ${formatAmount(calculateTaxAmount(tax, component), currency)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderComponentDetails = (component, idx) => {
    const totalUsage = component?.usage;
    const pricingType = component?.pricing_type;
    const meterName = component?.meter_name;
    let rate = '';
    const percentagePricing = component?.standard_or_percentage;
    if (percentagePricing?.percentage !== undefined) {
      const pct = percentagePricing?.percentage || 0;
      const fee = percentagePricing?.flatFee || 0;

      if (fee !== undefined && fee > 0) {
        rate = `${pct}% + ${currency} ${formatAmount(fee, currency)}`;
      } else {
        rate = `${pct}% + 0.00`;
      }
    }
    return (
      <div key={idx} className='meter-breakdown-section'>
        <div className='meter-breakdown-header'>
          <h4>
            {idx + 1}: {meterName}
          </h4>
        </div>

        <div className='meter-info-row'>
          <span className='meter-label'>Total Usage:</span>
          <span className='meter-value'>{formate(totalUsage)}</span>
        </div>

        {(pricingType === pricingTypes.TIERED || pricingType === pricingTypes.VOLUME) && component?.tiersConsumed && (
          <div className='tier-table-wrapper'>
            <table className='invoice-tier-table'>
              <thead>
                <tr>
                  <th>Tier Range</th>
                  <th>Units Consumed</th>
                  <th>Rate (Price/Unit + Fee)</th>
                  <th>Charged Amount</th>
                </tr>
              </thead>
              <tbody>
                {component?.tiersConsumed?.map?.((tier, tierIdx) => {
                  const lowerLimit = tier?.lowerLimit;
                  const upperLimit = tier?.upperLimit === null ? 'Infinity' : tier?.upperLimit;
                  const tierRange = `${lowerLimit} - ${upperLimit}`;
                  const tierRate = `${currency} ${formatAmount(tier?.pricePerUnit, currency)} + ${formatAmount(tier?.flatFee, currency)}`;

                  return (
                    <tr key={tier?._id || tierIdx}>
                      <td>{tierRange}</td>
                      <td>{tier?.consumedUnits}</td>
                      <td>{tierRate}</td>
                      <td>{`${currency} ${formatAmount(tier?.chargedAmount, currency)}`}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {pricingType === pricingTypes.FIXED && component?.fixed_retainer && (
          <div className='fixed-pricing-details'>
            <table className='invoice-tier-table'>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Units Consumed</th>
                  <th>Rate</th>
                  <th>Charged Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>No of Units</td>
                  <td>{totalUsage}</td>
                  <td>{`${currency} ${formatAmount(component?.fixed_retainer?.amount, currency)}`}</td>
                  <td>{`${currency} ${formatAmount(component?.amount_without_taxes || component?.amount, currency)}`}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {pricingType === pricingTypes.STANDARD && component?.standard_or_percentage && (
          <div className='standard-pricing-details'>
            <table className='invoice-tier-table'>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Units Consumed</th>
                  <th>Rate(Price/Unit)</th>
                  <th>Charged Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>No of Units</td>
                  <td>{totalUsage}</td>
                  <td>{`${currency} ${formatAmount(component?.standard_or_percentage?.amount, currency)}`}</td>
                  <td>{`${currency} ${formatAmount(component?.amount_without_taxes || component?.amount, currency)}`}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {pricingType === pricingTypes.PERCENTAGE && component?.standard_or_percentage && (
          <div className='percentage-pricing-details'>
            <table className='invoice-tier-table'>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Units Consumed</th>
                  <th>Rate (% + Fee)</th>
                  <th>Charged Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>No of Units</td>
                  <td>{totalUsage}</td>
                  <td>{rate || `${component?.standard_or_percentage?.percentage ?? '0'}%`}</td>
                  <td>{`${currency} ${formatAmount(component?.amount_without_taxes || component?.amount, currency)}`}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {renderTaxesTable(component)}
      </div>
    );
  };

  return (
    <div className='pricing-model-breakdown'>
      <div className='pricing-model-header'>
        <span className='model-number'>
          {modelNumber}: {pricingModel?.pricing_model_name}
        </span>
      </div>

      <div className='pricing-model-content'>{renderSummaryTable()}</div>
      {pricingModel?.components && pricingModel?.components?.length > 0 && (
        <div className='meters-breakdown-wrapper'>
          {pricingModel?.components?.map((component, idx) => (
            <React.Fragment key={component?.meter_id || idx}>{renderComponentDetails(component, idx)}</React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default PricingModelBreakdown;
